import { Component, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomerVehiclePromotions, PromoDetails } from '@pos-app/data';
import { NgbInputDatepicker, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '@pos-app/core-ui/lib/services/api.service';
import { select, Store } from '@ngrx/store';
import { CoreUiPartialState, isExternalUser, isInternalUser } from '@pos-app/core-ui';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { NgIf, NgFor } from '@angular/common';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-customers-vehicles-promotions',
    templateUrl: './customers-vehicles-promotions.component.html',
    styleUrls: ['./customers-vehicles-promotions.component.scss'],
    imports: [CoreUiModule, NgIf, NgSelectComponent, FormsModule, NgFor, NgOptionComponent, ReactiveFormsModule, MatIcon, NgbInputDatepicker, AsyncPipe]
})
export class CustomersVehiclesPromotionsComponent implements OnInit {
  @ViewChild('content') modalBox: any;

  promotionsDetailForm = false;
  allowEdit = true;
  customerVehiclePromotionsForm!: UntypedFormGroup;
  customerVehiclePromotionList: PromoDetails[] = [];
  currentIndex = 0;
  currentIndexForVehicle = 0;
  isShowText = true;
  managePromotion = false;
  currentPromoCode = '';
  isNewPromotion = false;
  private _loading$ = new BehaviorSubject<boolean>(false);
  loading$ = this._loading$.asObservable();
  vehiclePromotionList: PromoDetails[] = [];
  customerVehicleDetails!: CustomerVehiclePromotions;
  isAddingPromotionForVehicle = false;
  public unSubscribe$ = new Subject<void>();
  isInternalUser: boolean;
  addingFirstPromotion: boolean = false;
  formValid: boolean = false;
  initialFormValue: any;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private toastr: ToastrService,
    private apiService: ApiService,
    private modalService: NgbModal,
    private coreUiStore: Store<CoreUiPartialState>
  ) {}

  ngOnInit(): void {
    //check if the user is internal or external
    this.coreUiStore.pipe(select(isInternalUser), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.isInternalUser = data;
      this.allowEdit = this.isInternalUser;
    });

    this.customerVehiclePromotionsForm = this.formBuilder.group({
      promotionCode: [''],
      promotionName: [''],
      promotionShortDescription: [''],
      promotionTerms: [''],
      startingDate: ['', Validators.required],
      endingDate: ['', Validators.required],
      customerNumber: [''],
      customerName: [''],
      vehicleShortDescription: [''],
      vehicleColour: [''],
      lastChangedBySiteCode: [''],
      lastChangedByUser: [''],
      lastChangedDate: [''],
      customerVehicleID: [''],
      startingOdometer: [0],
    });

    // Helper to validate endingDate >= startingDate
    const validateDates = () => {
      const startingDate = this.customerVehiclePromotionsForm.get('startingDate')?.value;
      const endingDate = this.customerVehiclePromotionsForm.get('endingDate')?.value;
      if (endingDate && startingDate && moment(endingDate, 'DD/MM/YYYY').isBefore(moment(startingDate, 'DD/MM/YYYY'))) {
        this.customerVehiclePromotionsForm.get('endingDate')?.setErrors({ invalid: true });
        this.toastr.error('Ending date must be greater than or equal to Starting date', 'Error');
      } else {
        this.customerVehiclePromotionsForm.get('endingDate')?.setErrors(null);
      }
    };

    this.customerVehiclePromotionsForm.get('endingDate')?.valueChanges.subscribe(validateDates);
    this.customerVehiclePromotionsForm.get('startingDate')?.valueChanges.subscribe(validateDates);
    this.customerVehiclePromotionsForm.valueChanges.subscribe(() => {
      // Only set errors, don't show toast on every value change
      const startingDate = this.customerVehiclePromotionsForm.get('startingDate')?.value;
      const endingDate = this.customerVehiclePromotionsForm.get('endingDate')?.value;
      if (endingDate && startingDate && moment(endingDate, 'DD/MM/YYYY').isBefore(moment(startingDate, 'DD/MM/YYYY'))) {
        this.customerVehiclePromotionsForm.get('endingDate')?.setErrors({ invalid: true });
      } else {
        this.customerVehiclePromotionsForm.get('endingDate')?.setErrors(null);
      }
    });

    //on form touch set formValid to true
    this.customerVehiclePromotionsForm.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$)).subscribe((val) => {
      this.compareValues();
    });
  }
  compareValues() {
    const currentFormValue = this.customerVehiclePromotionsForm.getRawValue();
    if (JSON.stringify(currentFormValue) !== JSON.stringify(this.initialFormValue)) {
      this.formValid = true;
    } else {
      this.formValid = false;
    }
  }

  customerVehiclePromotion(customerVehicleDetails: CustomerVehiclePromotions): void {
    this.customerVehicleDetails = customerVehicleDetails;
    this.promotionsDetailForm = true;
    this.customerVehiclePromotionList = customerVehicleDetails?.promotions || [];
    this.patchValue(this.customerVehiclePromotionList);
  }

  patchValue(customerVehiclePromotionList: PromoDetails[]) {
    this.customerVehiclePromotionsForm.patchValue({
      promotionCode: customerVehiclePromotionList[this.currentIndex]?.promotionCode || '',
      promotionName: customerVehiclePromotionList[this.currentIndex]?.promotionName || '',
      promotionShortDescription: customerVehiclePromotionList[this.currentIndex]?.promotionShortDescription || '',
      promotionTerms: customerVehiclePromotionList[this.currentIndex]?.promotionTerms || '',
      startingDate: customerVehiclePromotionList[this.currentIndex]?.effectiveDate || '',
      endingDate: customerVehiclePromotionList[this.currentIndex]?.expiryDate || '',
      lastChangedBySiteCode: customerVehiclePromotionList[this.currentIndex]?.lastChangedBySiteCode || '',
      lastChangedByUser: customerVehiclePromotionList[this.currentIndex]?.lastChangedByUser || '',
      lastChangedDate: customerVehiclePromotionList[this.currentIndex]?.lastChangedDate || '',
      startingOdometer: customerVehiclePromotionList[this.currentIndex]?.startingOdometer || 0,
    });
    this.initialFormValue = this.customerVehiclePromotionsForm.getRawValue();
    this.compareValues();
  }

  changeIndex(increment: number) {
    this.currentIndex = this.currentIndex + increment;
    this.patchValue(this.customerVehiclePromotionList);
  }
  /**
   * Sets the current promotion index and updates the form with the selected promotion's details.
   * Removes validators from promotion fields except for startingDate and endingDate.
   * @param index Index of the promotion to display/edit.
   */
  setIndex(index: number): void {
    this.currentIndex = index;
    this.isNewPromotion = false;
    this.patchValue(this.customerVehiclePromotionList);

    // Remove validators from promotion fields (these are only required for new promotions)
    this.customerVehiclePromotionsForm.get('promotionCode')?.clearValidators();
    this.customerVehiclePromotionsForm.get('promotionName')?.clearValidators();
    this.customerVehiclePromotionsForm.get('promotionShortDescription')?.clearValidators();
    this.customerVehiclePromotionsForm.get('promotionTerms')?.clearValidators();

    // Ensure date fields remain required
    this.customerVehiclePromotionsForm.get('startingDate')?.setValidators([Validators.required]);
    this.customerVehiclePromotionsForm.get('endingDate')?.setValidators([Validators.required]);
    this.customerVehiclePromotionsForm.updateValueAndValidity();
  }

  setIndexForPromotionToAdd(index: number) {
    this.currentIndexForVehicle = index;
    this.isNewPromotion = false;
  }
  onClose() {
    this.managePromotion = false;
    this.isNewPromotion = false;
    this.promotionsDetailForm = false;
    this.customerVehiclePromotionList = [];
    this.currentIndex = 0;
    this.customerVehiclePromotionsForm.reset();
  }
  /**
   * Saves the current promotion details for the customer vehicle.
   * Updates the promotion list with form data, prepares payload, and calls API.
   */
  onSave() {
    if (!this.addingFirstPromotion) {
      if (!this.customerVehiclePromotionsForm.valid) {
        // Optionally, show validation error toast here
        return;
      }

      const formData = this.customerVehiclePromotionsForm.value;

      // Update the current promotion in the list with form data
      if (this.currentIndex !== null && this.currentIndex >= 0 && this.currentIndex < this.customerVehiclePromotionList.length) {
        this.customerVehiclePromotionList[this.currentIndex] = {
          ...this.customerVehiclePromotionList[this.currentIndex],
          ...formData,
          // Ensure effectiveDate and expiryDate are mapped from form fields
          effectiveDate: formData.startingDate,
          expiryDate: formData.endingDate,
        };
      }
    }

    // Prepare payload for API: only required fields
    const apiData = {
      customerVehicleID: this.customerVehicleDetails.customerVehicleID,
      promotionArray: this.customerVehiclePromotionList.map((item) => ({
        promotionCode: item.promotionCode,
        startingOdometer: item.startingOdometer ?? 0,
        startingDate: item.effectiveDate,
        endingDate: item.expiryDate,
      })),
    };

    this._loading$.next(true);
    this.apiService.maintainVehiclePromotionByCustomerVehicle(apiData).subscribe({
      next: (res) => {
        this._loading$.next(false);
        if (res?.ErrorFlag === '0') {
          this.toastr.success('Customer Vehicle Promotion updated successfully', 'Success');
          this.patchValue(this.customerVehiclePromotionList);
          this.onClose();
        } else {
          this.toastr.error(res?.ErrorMessage || 'Error updating Customer Vehicle Promotion', 'Error');
        }
      },
      error: () => {
        this._loading$.next(false);
        this.toastr.error('Network error while updating Customer Vehicle Promotion', 'Error');
      },
    });
  }
  showResultText(event: boolean) {
    if (!event) {
      this.isShowText = false;
    } else {
      this.isShowText = true;
    }
  }

  openCalander(datePicker: NgbInputDatepicker) {
    datePicker.toggle();
  }

  showManagePromotion() {
    this.managePromotion = true;
    this._loading$.next(true);
    this.apiService.vehiclePromotions().subscribe((res) => {
      this.promotionsDetailForm = true;
      this.customerVehiclePromotionList = res.SearchResults;
      this.patchValue(this.customerVehiclePromotionList);
      this._loading$.next(false);
    });
  }

  showVehicleSearchForPromotion() {
    this.managePromotion = false;
    this.promotionsDetailForm = false;
    this.customerVehiclePromotionList = [];
    this.currentIndex = 0;
    this.customerVehiclePromotionsForm.reset();
  }
  /**
   * Prepares the form for adding a new promotion.
   * Resets the form, sets required validators for all promotion fields,
   * and marks the state as "new promotion".
   */
  addNewPromotion(): void {
    this.isNewPromotion = true;
    this.currentIndex = null;
    this.promotionsDetailForm = true;
    this.customerVehiclePromotionsForm.reset();

    // Set required validators for all promotion fields
    ['promotionCode', 'promotionName', 'promotionShortDescription', 'promotionTerms', 'startingDate', 'endingDate'].forEach((field) => {
      this.customerVehiclePromotionsForm.get(field)?.setValidators([Validators.required]);
      this.customerVehiclePromotionsForm.get(field)?.updateValueAndValidity({ onlySelf: true });
    });

    this.customerVehiclePromotionsForm.updateValueAndValidity();
  }

  /**
   * Initiates the process to add a new promotion to the vehicle.
   * Fetches available vehicle promotions, filters out those already added,
   * and opens a modal for selection.
   */
  addNewPromotionForVehicle(): void {
    this.isAddingPromotionForVehicle = true;
    this._loading$.next(true);
    // Fetch all available vehicle promotions from API
    this.apiService.vehiclePromotions().subscribe({
      next: (res) => {
        this._loading$.next(false);
        this.promotionsDetailForm = true;

        // Ensure SearchResults is an array, fallback to empty array if not
        const fetchedPromotions: PromoDetails[] = Array.isArray(res?.SearchResults) ? res.SearchResults : [];

        // Filter out promotions already present in customerVehiclePromotionList
        this.vehiclePromotionList = fetchedPromotions.filter(
          (promo) => !this.customerVehiclePromotionList.some((p) => p.promotionCode === promo.promotionCode)
        );

        // Reset selection index
        this.currentIndexForVehicle = 0;

        if (this.vehiclePromotionList.length === 0) {
          // Inform user if no new promotions are available to add
          this.toastr.info('No new promotions available to add', 'Info');
          this.isAddingPromotionForVehicle = false;
          return;
        }

        // Open modal for user to select a promotion to add
        this.openModalBox();
      },
      error: () => {
        // Handle API/network error
        this.toastr.error('Failed to fetch vehicle promotions', 'Error');
        this.isAddingPromotionForVehicle = false;
        this._loading$.next(false);
      },
    });
  }

  openModalBox() {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        (_) => {
          this.isAddingPromotionForVehicle = false;
        },
        (_) => {
          this.isAddingPromotionForVehicle = false;
        }
      );
  }

  /**
   * Adds the selected promotion from vehiclePromotionList to the customer's vehicle promotion list.
   * Checks for duplicates before adding, shows success message, closes modal, and saves changes.
   */
  addPromotionToVehicle(): void {
    this.isAddingPromotionForVehicle = true;
    const selectedPromo = this.vehiclePromotionList[this.currentIndexForVehicle];
    if (selectedPromo) {
      // Prevent adding duplicate promotions
      const alreadyExists = this.customerVehiclePromotionList.some((promo) => promo.promotionCode === selectedPromo.promotionCode);
      if (alreadyExists) {
        this.toastr.warning('This promotion is already added to the vehicle.', 'Warning');
        this.modalService.dismissAll();
        this.isAddingPromotionForVehicle = false;
        return;
      }
      if (this.customerVehiclePromotionList.length === 0) {
        this.addingFirstPromotion = true;
      } else {
        this.addingFirstPromotion = false;
      }
      this.customerVehiclePromotionList.push(selectedPromo);
      this.modalService.dismissAll();
      this.currentIndexForVehicle = 0;
      this.isAddingPromotionForVehicle = false;
      this.isNewPromotion = false;
      this.onSave();
    }
  }

  resetInitialFormValue() {
    this.customerVehiclePromotion(this.customerVehicleDetails);
  }
}

